import { useEffect, useState } from "react";
import algosdk from "algosdk";
import { useWallet } from "../context/WalletContext";
import { useNavigate } from "react-router-dom";
import { useTransaction } from "../context/TransactionContext";
import { useNetwork } from "../context/NetworkContext";
import "../styles/wallet.css";
import "../styles/general.css";

interface MoxWalletActionsProps {
  setMessage: (msg: string, type?: "success" | "error") => void;
}

const MoxWalletActions = ({ setMessage }: MoxWalletActionsProps) => {
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [selectedAsset, setSelectedAsset] = useState<"ALGO" | number>("ALGO");
  const [algoBalance, setAlgoBalance] = useState(0);
  const [asaBalances, setAsaBalances] = useState<Record<number, number>>({});

  const { openTransactionModal } = useTransaction();
  const { account } = useWallet();
  const navigate = useNavigate();

  const { network, algodClient, ASA_HLK, ASA_UMQ } = useNetwork(); 

  const ASA_LIST = [
    {
      id: ASA_UMQ,
      name: "UMQ",
      decimals: 9,
      image: "/images/umq.avif",
    },
    {
      id: ASA_HLK,
      name: "HLK",
      decimals: 9,
      image: "/images/holkan.avif",
    },
  ];

  const fetchBalances = async () => {
    if (!account || !algodClient) return;

    try {
      const info = await algodClient.accountInformation(account.addr).do();
      setAlgoBalance(info.amount);

      const balances: Record<number, number> = {};
      for (const asset of ASA_LIST) {
        const found = info.assets.find((a: any) => a["asset-id"] === asset.id);
        balances[asset.id] = found ? found.amount : 0;
      }
      setAsaBalances(balances);
    } catch (e) {
      console.error("Error consultando balances:", e);
      setMessage("❌ Error al consultar balances", "error");
    }
  };

  // ✅ Se recarga automáticamente al cambiar cuenta o red
  useEffect(() => {
    if (account) fetchBalances();
  }, [account, network, algodClient]);

  const sendTransaction = async () => {
    if (!account) return setMessage("⚠️ Conecta tu cuenta", "error");

    const numericAmount = parseFloat(amount);
    if (!recipient || isNaN(numericAmount) || numericAmount <= 0) {
      return setMessage("⚠️ Ingresa datos válidos", "error");
    }

    try {
      const params = await algodClient.getTransactionParams().do();
      const isASA = selectedAsset !== "ALGO";
      let txn;

      if (isASA) {
        const asset = ASA_LIST.find((a) => a.id === selectedAsset);
        if (!asset) return setMessage("⚠️ ASA no válido", "error");

        txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
          from: account.addr,
          to: recipient,
          amount: Math.floor(numericAmount * 10 ** asset.decimals),
          assetIndex: asset.id,
          suggestedParams: params,
        });
      } else {
        txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          from: account.addr,
          to: recipient,
          amount: Math.floor(numericAmount * 1e6),
          suggestedParams: params,
        });
      }

      const signed = txn.signTxn(account.sk);
      const { txId } = await algodClient.sendRawTransaction(signed).do();

      setMessage(`✅ Transacción enviada - TxID: ${txId}`, "success");
      fetchBalances();
    } catch (err) {
      console.error(err);
      setMessage("❌ Error al enviar transacción", "error");
    }
  };

  const handleOptIn = async () => {
    if (!account || typeof selectedAsset !== "number") return;
    try {
      const params = await algodClient.getTransactionParams().do();
      const txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: account.addr,
        to: account.addr,
        amount: 0,
        assetIndex: selectedAsset,
        suggestedParams: params,
      });
      const signed = txn.signTxn(account.sk);
      const { txId } = await algodClient.sendRawTransaction(signed).do();
      setMessage(`✅ Opt-in realizado - TxID: ${txId}`, "success");
      fetchBalances();
    } catch (err) {
      console.error(err);
      setMessage("❌ Error en opt-in", "error");
    }
  };

  const handleSend = () => {
    openTransactionModal({
      recipient,
      amount: 0,
      asset:
        selectedAsset === "ALGO"
          ? "ALGO"
          : { name: "UMQ", id: ASA_UMQ, decimals: 9 },
      onConfirm: () => {
        sendTransaction();
      },
    });
  };

  return (
    <div className="register-container">
      <div>
        <h1>
          <img
            src="/images/icon-192.png"
            alt="Mox Logo"
            width={42}
            height={42}
            style={{
              borderRadius: "50%",
              verticalAlign: "middle",
              marginRight: 8,
              backgroundColor: "grey",
            }}
          />
          Mox Wallet
        </h1>

        {account ? (
          <>
            <p>
              <strong>Cuenta:</strong>{" "}
              {account.addr.slice(0, 6)}...{account.addr.slice(-4)}
            </p>

            <div>
              {ASA_LIST.map((a) => (
                <div key={a.id}>
                  <span>
                    {((asaBalances[a.id] || 0) / 10 ** a.decimals).toFixed(2)}{" "}
                    {a.name}
                  </span>
                  <img
                    src={a.image}
                    alt={a.name}
                    width={30}
                    height={30}
                    className="token-icon"
                    style={{
                      borderRadius: "50%",
                      verticalAlign: "middle",
                      marginLeft: 8,
                      backgroundColor: "grey",
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="balance-item">
              <span>{(algoBalance / 1e6).toFixed(4)} ALGO</span>
              <img
                src="/images/algo_new.png"
                alt="ALGO"
                width={30}
                height={30}
                className="token-icon"
                style={{
                  fontSize: 10,
                  borderRadius: "50%",
                  verticalAlign: "middle",
                  marginLeft: 8,
                  backgroundColor: "grey",
                }}
              />
            </div>

            <hr className="my-4" />

            <div className="info-wallet">
              <h4 className="text-lg font-semibold mb-2">Enviar</h4>
              <input
                type="text"
                placeholder="Dirección de destino"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="wallet-input"
              />
              <input
                type="number"
                placeholder="Ingrese valor"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="wallet-input no-spinner"
              />
              <select
                className="wallet-input"
                value={selectedAsset}
                onChange={(e) =>
                  setSelectedAsset(
                    e.target.value === "ALGO"
                      ? "ALGO"
                      : parseInt(e.target.value)
                  )
                }
              >
                <option value="ALGO">ALGO</option>
                {ASA_LIST.map((asa) => (
                  <option key={asa.id} value={asa.id}>
                    {asa.name}
                  </option>
                ))}
              </select>
              <button
  className="button-primary"
  onClick={() =>
    openTransactionModal({
      recipient,
      amount: parseFloat(amount) || 0,
      asset: selectedAsset === "ALGO"
        ? "ALGO"
        : { name: "UMQ", id: ASA_UMQ, decimals: 9 },
      onConfirm: () => sendTransaction(),
    })
  }
>
  Enviar
</button>
              <button
                className="button-exit"
                onClick={() => navigate("/home")}
              >
                Volver
              </button>
              {selectedAsset !== "ALGO" && (
                <button
                  className="button-secondary"
                  onClick={handleOptIn}
                >
                  Opt-in al ASA
                </button>
              )}
            </div>
          </>
        ) : (
          <p>
          Conecta tu MoxWallet para continuar.{" "}
          <span
            style={{ color: "#1E90FF", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => navigate("/home")}
          >
            Volver a Home
          </span>
        </p>
        )}
      </div>
    </div>
  );
};

export default MoxWalletActions;
